<!--
  @component
  Generates an SVG multi-series line chart. It expects your data to be an array of objects, each with a `values` key that is an array of data objects.
 -->
<script>
  import { getContext } from "svelte";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";

  /**
   * @property {boolean} labeled - whether to display axis labels for this chart
   * @property {[string, string]} - what to display if labeled is true
   */
  let { labeled = false, labels = ["", ""] } = $props();

  const { data, x, y, z, xScale, xGet, yGet, yScale, custom } = getContext("LayerCake");

  //this takes our long data and groups it by category, then returns only the xGet and yGet values in each category
  let endpoints = $derived.by(() => {
    let ret = $data.reduce((acc, current) => {
      let key = $z(current);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push([$xGet(current), $yGet(current)]);
      return acc;
    }, {});
    return Object.values(ret);
  });

  let values = $derived.by(() => {
    let ret = $data.reduce((acc, current) => {
      let key = $z(current);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push([$x(current), $y(current)]);
      return acc;
    }, {});
    return Object.values(ret);
  });

  let highestFirstPointIndex = $derived.by((d) => {
    let firstValues = $data.filter((d) => d.type == 0).map((d) => d.value);
    return firstValues.indexOf(Math.max(...firstValues));
  });

  let highestSecondPointIndex = $derived.by((d) => {
    let secondValues = $data.filter((d) => d.type == 1).map((d) => d.value);
    return secondValues.indexOf(Math.max(...secondValues));
  });

  // this value is equal to higher-valued i at index j
  let highestIndices = $derived([highestFirstPointIndex, highestSecondPointIndex]);

  let lines = $derived(endpoints.map((d) => "M " + d[0] + " L " + d[1]));

  //
</script>

{#if $data}
  {#if labeled}
    <text class="axis-label" text-anchor="end" x={$xScale(0)} y={$yScale.range()[0] + 36}
      >{labels[0]}</text
    >
    <text class="axis-label" text-anchor="start" x={$xScale.range()[1]} y={$yScale.range()[0] + 36}
      >{labels[1]}</text
    >
  {/if}
  <line
    stroke={urbanColors.gray_shade_dark}
    stroke-width="1"
    x1={$xScale(0)}
    x2={$xScale(0)}
    y1={$yScale.range()[0]}
    y2={$yScale.range()[1]}
  ></line>
  <line
    stroke={urbanColors.gray_shade_dark}
    stroke-width="1"
    x1={$xScale.range()[1]}
    x2={$xScale.range()[1]}
    y1={$yScale.range()[0]}
    y2={$yScale.range()[1]}
  ></line>
  {#each lines as line, i}
    <g class="data-group">
      <path
        class="path-line"
        d={line}
        stroke={$custom.seriesColors[i]}
        stroke-width={$custom.seriesWidths[i]}
      ></path>
      {#each endpoints[i] as point, j}
        {@const isThisValueHigher = highestIndices[j] == i}
        <g class="one-circle">
          <!-- <line
            x1={j == 0 ? point[0] : point[0]}
            x2={j == 0 ? point[0] : point[0]}
            y1={isThisValueHigher ? point[1] - 4 : point[1] + 19}
            y2={isThisValueHigher ? point[1] - 21 : point[1] + 1}
            stroke={$custom.seriesColors[i]}
            stroke-width="3"
          ></line> -->
          <!-- <text
            class="under-label"
            fill={$custom.seriesColors[i]}
            text-anchor={j == 0 ? "end" : "start"}
            text-decoration={isThisValueHigher ? "underline" : "overline"}
            x={j == 0 ? point[0] - 11 : point[0] + 11}
            y={isThisValueHigher ? point[1] - 10 : point[1] + 16}
            >{$custom.format.format(values[i][j][1])}</text
          > -->
          <text
            class="label"
            text-anchor={j == 0 ? "end" : "start"}
            x={j == 0 ? point[0] - 6 : point[0] + 6}
            y={isThisValueHigher ? point[1] - 7 : point[1] + 16}
            fill={$custom.seriesColors[i] == "transparent" ? "transparent" : "black"}
            >{$custom.format.format(values[i][j][1])}</text
          >
          <circle
            class="visible-marker"
            cx={point[0]}
            cy={point[1]}
            r="4"
            fill={$custom.seriesColors[i]}
            stroke={$custom.seriesColors[i]}
            stroke-width="2"
          />
        </g>
      {/each}
    </g>
  {/each}
{:else}
  <p>{Object.entries(data)}</p>
{/if}

<style>
  .path-line {
    fill: none;
  }
  .axis-label {
    font-size: 14px;
    /* font-weight: 700; */
    font-style: italic;
  }
  text.under-label {
    text-underline-offset: 4px;
    text-decoration-thickness: 3px;
  }
</style>
